import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {Button, Dropdown} from 'antd';
import {useNavigate} from 'react-router-dom';
import {EllipsisOutlined} from '@ant-design/icons';
import {
  StyledInvoiceNavigationButtons,
  StyledInvoiceNavigationButtonsSpace,
} from './StyledInvoiceNavigationButtons';
import DeleteModal from '../../../DeleteModal';
import IconWrapper from '../../../IconWrapper';
import {ChevronLeftIcon, DownloadIcon, TrashIcon, WireIcon} from '../../../../icons';
import {alertActions} from '../../../../state/actions/alert.actions';
import {invoicesActions} from '../../../../state/actions/invoice.actions';
import {firebaseEvents} from '../../../../snippets/firebase';
import {fileHelpers, invoicesHelpers, systemHelpers} from '../../../../utils/helpers';
import routes from '../../../../routes/routes.json';
import {colorsStyles} from '../../../../utils/styles';

const extractButtonProps = (props = {}) => {
  const { available, ...filtered } = props;
  return { available, filtered };
};

const InvoiceNavigationButtons = ({
  invoice,
  backButtonProps,
  neighborIds = { next: null, prev: null },
  payButtonProps,
  paymentRequestButtonProps,
  ...rest
}) => {
  const [deleteModalProps, setDeleteModalProps] = useState({loading: false, open: false});
  const [t] = useTranslation(['main', 'invoices']);
  const navigate = useNavigate();
  const [dropdownButtonProps, setDropdownButtonProps] = useState({});

  const dispatch = useDispatch();

  const { available: showBack, filtered: filteredBackProps } = extractButtonProps(backButtonProps);
  const { available: showPay, filtered: filteredPayProps } = extractButtonProps(payButtonProps);
  const { available: showPaymentRequest, filtered: filteredPaymentRequestProps } = extractButtonProps(paymentRequestButtonProps);

  const handleDelete = () => setDeleteModalProps({...deleteModalProps, open: true});

  const handleCancelDeleteInvoiceModal = () => {
    setDeleteModalProps({...deleteModalProps, loading: false, open: false});
  }

  const handleOkDeleteInvoiceModal = () => {
    const id = invoice?.id;
    if (!id) return;

    setDeleteModalProps({...deleteModalProps, loading: true});
    dispatch(invoicesActions.batchDeleteInvoices(
      [id],
      () => {
        const nextId = neighborIds?.next;
        const redirectPageUrl = nextId
          ? invoicesHelpers.getInvoiceDetailsLink(nextId)
          : routes.invoicesList;

        alertActions.success(t('invoices:alert.successInvoiceDeleteMessage'));
        handleCancelDeleteInvoiceModal();
        systemHelpers.logEvent(firebaseEvents.INVOICES_DELETE_INVOICE);
        navigate(redirectPageUrl);
      },
      handleCancelDeleteInvoiceModal
    ));
  }

  const setDropdownLoading = (loading) => setDropdownButtonProps({...dropdownButtonProps, loading});

  const handleDownload = () => {
    if (!invoice?.id) return;

    setDropdownLoading(true);

    dispatch(invoicesActions.downloadInvoice(
      invoice.id,
      (data) => {
        fileHelpers.saveFile(data);
        setDropdownButtonProps({...dropdownButtonProps, loading: false});
        systemHelpers.logEvent(firebaseEvents.INVOICES_DOWNLOAD_INVOICE);
        setDropdownLoading(false);
      },
      () => {
        setDropdownLoading(false);
        alertActions.error(t('invoices:alert.errorInvoiceDownloadMessage'));
      }
    ));
  }

  const handleMenuClick = ({key}) => ({
    delete: handleDelete,
    download: handleDownload,
  })[key]?.();

  const renderIcon = (icon) => <IconWrapper size={16} color={colorsStyles.neutral_7}>{icon}</IconWrapper>;

  const menuProps = {
    onClick: handleMenuClick,
    items: [
      {
        label: t('download'),
        key: 'download',
        icon: renderIcon(<DownloadIcon />),
      },
      {
        label: t('delete'),
        key: 'delete',
        icon: renderIcon(<TrashIcon />),
      },
    ].filter(Boolean),
  };

  return (
    <StyledInvoiceNavigationButtons {...rest}>

      <StyledInvoiceNavigationButtonsSpace size='small' wrap='true'>
        {showBack && (
          <Button
            icon={<ChevronLeftIcon />}
            size='large'
            {...filteredBackProps}
          >
            {t('back')}
          </Button>
        )}

        {showPaymentRequest && (
          <Button
            icon={<WireIcon />}
            size='large'
            type='primary'
            {...filteredPaymentRequestProps}
          >
            {t('request')} {t('payment')}
          </Button>
        )}

        {showPay && (
          <Button
            icon={<WireIcon />}
            size='large'
            type='primary'
            {...filteredPayProps}
          >
            {t('pay')} {t('now')}
          </Button>
        )}

        <Dropdown menu={menuProps} placement='bottomRight' disabled={dropdownButtonProps?.loading}>
          <Button size='middle' icon={<EllipsisOutlined />} {...dropdownButtonProps} />
        </Dropdown>

      </StyledInvoiceNavigationButtonsSpace>

      <DeleteModal
        description={t(`invoices:modal.delete.description`)}
        onCancel={handleCancelDeleteInvoiceModal}
        onOk={handleOkDeleteInvoiceModal}
        title={t(`invoices:modal.delete.title`)}
        {...deleteModalProps}
      />
    </StyledInvoiceNavigationButtons>
  );
}

InvoiceNavigationButtons.propTypes = {
  invoice: PropTypes.object,
  backButtonProps: PropTypes.object,
  neighborIds: PropTypes.shape({
    next: PropTypes.string,
    prev: PropTypes.string
  }),
  payButtonProps: PropTypes.object,
}

export default InvoiceNavigationButtons;
